import type { LevelRecipe } from '../model/recipe';
import type { PuzzleAnalysis } from '../solver/analysis';
import type { HardConstraints, ScoringWeights, TuningConfig } from './config/tuning';

/**
 * Candidate scoring.
 *
 * Solvable is not the same as interesting, so candidates are scored against the *target
 * profile* in the recipe rather than by maximising raw difficulty. Hard constraints reject
 * outright; the remaining score is 1 minus a weighted sum of normalised deviations.
 */

export interface CandidateScore {
  readonly accepted: boolean;
  readonly score: number;
  readonly rejections: readonly string[];
  readonly penalties: Readonly<Record<string, number>>;
}

export function scoreCandidate(
  analysis: PuzzleAnalysis,
  recipe: LevelRecipe,
  tuning: TuningConfig,
  options: { readonly duplicateSignature?: boolean } = {},
): CandidateScore {
  const constraints = tuning.hardConstraints[recipe.difficultyBand];
  const rejections = collectRejections(analysis, recipe, constraints);

  const weights: ScoringWeights = tuning.scoring;
  const occupancyPermille = Math.round(analysis.occupancyRatio * 1000);
  const targetOccupancy = recipe.targetOccupancyPermille;

  const penalties: Record<string, number> = {
    occupancy: weights.occupancy * normalise(Math.abs(occupancyPermille - targetOccupancy), 300),
    initialMoves:
      weights.initialMoves * rangePenalty(analysis.initialLegalMoves, recipe.initialMoveRange),
    meanMoves: weights.meanMoves * rangePenalty(analysis.meanLegalMoves, recipe.averageMoveRange),
    forcedRatio:
      weights.forcedRatio *
      normalise(
        Math.abs(
          Math.round(analysis.forcedStepRatio * 1000) - recipe.targetForcedStepRatioPermille,
        ),
        400,
      ),
    dependencyDepth:
      weights.dependencyDepth *
      normalise(Math.abs(analysis.dependencyDepth - recipe.targetDependencyDepth), 14),
    dispersion: weights.dispersion * (1 - analysis.spatialDispersion),
    coverage: weights.coverage * (1 - analysis.silhouetteCoverage),
    repetition: weights.repetition * analysis.repetitionPenalty,
    directionBalance: weights.directionBalance * analysis.directionalImbalance,
    unlockRhythm: weights.unlockRhythm * unlockRhythmPenalty(analysis),
    // Reward boards whose mean path length lands inside the recipe's range.
    arrowLength:
      weights.arrowLength *
      rangePenalty(analysis.meanArrowLength, [
        recipe.arrowLengthRange[0],
        recipe.arrowLengthRange[1],
      ]),
  };

  let total = 0;
  for (const key of Object.keys(penalties).sort()) {
    total += penalties[key];
  }
  if (options.duplicateSignature === true) total += 0.25;

  const score = clamp01(1 - total);
  return { accepted: rejections.length === 0, score, rejections, penalties };
}

function collectRejections(
  analysis: PuzzleAnalysis,
  recipe: LevelRecipe,
  constraints: HardConstraints,
): string[] {
  const rejections: string[] = [];

  if (!analysis.solvable) rejections.push('not-solvable');
  if (analysis.arrowCount < constraints.minArrowCount) rejections.push('too-few-arrows');
  if (analysis.occupiedCellCount < constraints.minOccupiedCells) rejections.push('too-few-cells');
  if (analysis.initialLegalMoves < constraints.minInitialMoves) {
    rejections.push(analysis.initialLegalMoves === 0 ? 'zero-opening-moves' : 'too-few-openings');
  }
  if (analysis.initialLegalMoves > constraints.maxInitialMoves)
    rejections.push('too-many-openings');
  if (
    analysis.arrowCount > 0 &&
    analysis.initialLegalMoves * 1000 >
      analysis.arrowCount * constraints.maxInitialMoveRatioPermille
  ) {
    rejections.push('board-too-open');
  }
  if (analysis.dependencyDepth < constraints.minDependencyDepth) {
    rejections.push('insufficient-dependency-structure');
  }
  if (Math.round(analysis.forcedStepRatio * 1000) > constraints.maxForcedStepRatioPermille) {
    rejections.push('too-forced');
  }
  const occupancyDeviation = Math.abs(
    Math.round(analysis.occupancyRatio * 1000) - recipe.targetOccupancyPermille,
  );
  if (occupancyDeviation > constraints.maxOccupancyDeviationPermille) {
    rejections.push('occupancy-off-target');
  }
  if (analysis.silhouetteCoverage < constraints.minSilhouetteCoverage) {
    rejections.push('silhouette-obscured');
  }
  if (analysis.repetitionPenalty > constraints.maxRepetitionPenalty) {
    rejections.push('repetitive-directions');
  }
  if (analysis.directionalImbalance > constraints.maxDirectionalImbalance) {
    rejections.push('directional-imbalance');
  }
  // Dust: on a board of any size, most arrows sitting in their own dependency component
  // means there is no puzzle to unpick, just a list of independent taps.
  if (analysis.arrowCount >= 24 && analysis.componentCount * 3 > analysis.arrowCount * 2) {
    rejections.push('fragmented-clusters');
  }
  // Winding arrows are the point: a board of stubs where the recipe asked for long paths has
  // lost the character of the level. The threshold sits below the requested minimum because
  // a dense silhouette legitimately cuts some paths short.
  if (
    recipe.arrowLengthRange[1] >= 4 &&
    analysis.meanArrowLength * 10 < recipe.arrowLengthRange[0] * 7
  ) {
    rejections.push('arrows-too-short');
  }
  return rejections;
}

/**
 * Rewards a steady rhythm of unlocks. Boards where removals rarely free anything play as
 * flat busywork; boards where a single removal opens everything collapse into a cascade.
 */
function unlockRhythmPenalty(analysis: PuzzleAnalysis): number {
  if (analysis.arrowCount === 0) return 1;
  const unlockRate = analysis.unlockEvents / analysis.arrowCount;
  const sparsePenalty = unlockRate < 0.5 ? (0.5 - unlockRate) * 2 : 0;
  const burstPenalty = normalise(Math.max(0, analysis.largestUnlockBurst - 4), 10);
  return clamp01(sparsePenalty * 0.6 + burstPenalty * 0.4);
}

function rangePenalty(value: number, range: readonly [number, number]): number {
  if (value >= range[0] && value <= range[1]) return 0;
  const distance = value < range[0] ? range[0] - value : value - range[1];
  const span = Math.max(1, range[1] - range[0]);
  return normalise(distance, span * 2);
}

function normalise(value: number, scale: number): number {
  if (scale <= 0) return 0;
  return clamp01(value / scale);
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
